import { AlignJustify, Wrench, Eye, ChevronLeft, Save } from "lucide-react";
import { Button } from "../../../../../components/ui/button";
import { Card, CardContent } from "../../../../../components/ui/card";
import { Checkbox } from "../../../../../components/ui/checkbox";
import { Input } from "../../../../../components/ui/input";
import { Label } from "../../../../../components/ui/label";
import { MultiSelect } from "../../../../../components/ui/multi-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../../../components/ui/tabs";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import type { DashboardProject } from "../../../types/project";
import { SpecificationDocument } from "../../../types";
import { Specifications } from "../../../mocks/Specification";
import { Materials } from "../../../types/materials";
import { materialsList, pipeSizes } from "../../../mocks/material";
import { useTeamMembers } from "../../../hooks/useTeamMembers";
import { DialogContent, Dialog } from "../../../../../components/ui/dialog";
import { DialogHeader, DialogTitle } from "../../../../../components/ui/dialog";
import { QRScanner } from "./QRScanner";
import { tabConfig } from "./tabConfig";
import { ProductionTab } from "./tabs/ProductionTab";
import { FitmentTab } from "./tabs/FitmentTab";
import { VisualInspectionTab } from "./tabs/VisualInspectionTab";
import { AlignmentTab } from "./tabs/AlignmentTab";
import { Icon } from "@radix-ui/react-select";
import { Cone } from "lucide-react";

interface AssemblyProductionDashboardProps {
  project: DashboardProject;
  onBack: () => void;
  selectedAssembly: string | null;
  setSelectedAssembly: (assembly: string | null) => void;
}

export const AssemblyProduction: React.FC<AssemblyProductionDashboardProps> = ({ project, onBack, selectedAssembly, setSelectedAssembly }) => {
  const {
    teamMembers,
    selectedTeamMember,
  } = useTeamMembers();

  const [specifications, setSpecifications] = useState<SpecificationDocument[]>(Specifications);
  const [materials, setMaterials] = useState<Materials[]>(materialsList);
  const [assemblyEditTab, setAssemblyEditTab] = useState<'production' | 'fitment' | 'visual' | 'alignment'>('production');
  const [pipeSize, setPipeSize] = useState<typeof pipeSizes[number][]>(pipeSizes);
  const [selectedGasIds, setSelectedGasIds] = useState<string[]>([]);
  const [selectedWireIds, setSelectedWireIds] = useState<string[]>([]);
  const [customerSignature, setCustomerSignature] = useState<string>('');
  const [qcSignature, setQcSignature] = useState<string>('');
  const [selectedForgeId, setSelectedForgeId] = useState<string | null>(null);
  const [selectedPipeId, setSelectedPipeId] = useState<string | null>(null);
  const [distanceFromPipeEnd, setDistanceFromPipeEnd] = useState<number | null>(null);
  const [isQRScannerOpen, setIsQRScannerOpen] = useState(false);
  const [scanningFor, setScanningFor] = useState<'forge' | 'pipe' | null>(null);
  const [selectedPipeSize, setSelectedPipeSize] = useState<number>(0);

  // Create options for gas and wire lot dropdowns
  const gasOptions = useMemo(() => 
    materials
      .filter(m => 
        m.category === 'Gas' && 
        m.pipeId === `pipe-${selectedPipeSize}` &&
        m.status === 'Available'
      )
      .map(gas => ({
        value: gas.id,
        label: gas.gasSerialNumber || ''
      })), [materials, selectedPipeSize]);

  const wireLotOptions = useMemo(() => 
    materials
      .filter(m => 
        m.category === 'Wire Lot' && 
        m.pipeId === `pipe-${selectedPipeSize}` &&
        m.status === 'Available'
      )
      .map(wire => ({
        value: wire.id,
        label: wire.lotNumber || ''
      })), [materials, selectedPipeSize]);

  const handleForgeChange = (value: string) => {
    setSelectedForgeId(value);
  };

  const handlePipeChange = (value: string) => {
    setSelectedPipeId(value);
  };

  const handleDistanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value ? Number(e.target.value) : null;
    setDistanceFromPipeEnd(value);
  };

  const handleQRScan = (type: 'forge' | 'pipe') => {
    setScanningFor(type);
    setIsQRScannerOpen(true);
  };

  const handleQRResult = (result: string) => {
    if (scanningFor === 'forge') {
      const forge = materials.find(m => 
        m.category === 'Forge' && 
        m.forgeSerialNumber === result && 
        m.status === 'Available'
      );
      if (forge) {
        setSelectedForgeId(forge.id);
      } else {
        toast.error('Invalid or unavailable forge QR code');
      }
    } else if (scanningFor === 'pipe') {
      const pipe = materials.find(m => 
        m.category === 'Pipe' && 
        m.pipeNo === result && 
        m.status === 'Available'
      );
      if (pipe) {
        setSelectedPipeId(pipe.id);
      } else {
        toast.error('Invalid or unavailable pipe QR code');
      }
    }
    setIsQRScannerOpen(false);
    setScanningFor(null);
  };

  const renderTabContent = (tabId: string) => {
    switch (tabId) {
      case 'production':
        return (
          <ProductionTab
            specifications={specifications}
            materials={materials}
            selectedPipeSize={selectedPipeSize}
            selectedGasIds={selectedGasIds}
            setSelectedGasIds={setSelectedGasIds}
            selectedWireIds={selectedWireIds}
            setSelectedWireIds={setSelectedWireIds}
            selectedForgeId={selectedForgeId}
            handleForgeChange={handleForgeChange}
            selectedPipeId={selectedPipeId}
            handlePipeChange={handlePipeChange}
            distanceFromPipeEnd={distanceFromPipeEnd}
            handleDistanceChange={handleDistanceChange}
            handleQRScan={handleQRScan}
            customerSignature={customerSignature}
            setCustomerSignature={setCustomerSignature}
            qcSignature={qcSignature}
            setQcSignature={setQcSignature}
          />
        );
      case 'fitment':
        return <FitmentTab />;
      case 'visual':
        return <VisualInspectionTab />;
      case 'alignment':
        return <AlignmentTab />;
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => setSelectedAssembly(null)}
              className="hover:bg-gray-100 p-2 rounded-lg transition-colors"
              title="Go back"
              variant="outline"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h2 className="text-xl font-semibold">{selectedAssembly}</h2>
              <p className="text-sm text-gray-500">
                {pipeSizes[selectedPipeSize]?.size}" × {pipeSizes[selectedPipeSize]?.wallThickness}" Buckle Arrestor
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => setSelectedAssembly(null)}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              variant="outline"
            >
              Cancel
            </Button>
            <Button className="px-4 py-2 bg-[#e04530] text-white rounded-lg hover:bg-[#e04530]/90">
              <Save className="h-4 w-4 mr-2" />
              Save 
            </Button>
          </div>
        </div>
      </div>

      <Tabs value={assemblyEditTab} onValueChange={(value: any) => setAssemblyEditTab(value)} className="w-full">
      <div className="sticky top-0 bg-white rounded-t-lg z-10 border-b border-gray-200 rounded-t-lg">
      <TabsList className="h-12 w-full justify-start gap-8 border-b border-gray-200 bg-transparent px-4 py-8">
            {tabConfig.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="data-[state=active]:border-[#e04530] data-[state=active]:text-[#e04530] relative px-1 pb-3 pt-2 text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px]"
              >
                <div className="flex items-center gap-2">
                  {tab.id === 'production' && <Cone className="h-4 w-4" />}
                  {tab.id === 'fitment' && <Wrench className="h-4 w-4" />}
                  {tab.id === 'visual' && <Eye className="h-4 w-4" />}
                  {tab.id === 'alignment' && <AlignJustify className="h-4 w-4" />}  
                  {tab.label}
                </div>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="p-6">
          {tabConfig.map((tab) => (
            <TabsContent key={tab.id} value={tab.id} className="mt-0">
              {renderTabContent(tab.id)}
            </TabsContent>
          ))}
        </div>
      </Tabs>

      {/* QR Scanner Dialog */}
      <Dialog open={isQRScannerOpen} onOpenChange={setIsQRScannerOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Scan QR Code for {scanningFor === 'forge' ? 'Forge' : 'Pipe'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <QRScanner
              onResult={handleQRResult}
              onError={(error: string) => {
                toast.error(error);
                setIsQRScannerOpen(false);
                setScanningFor(null);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}